import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetAvailablePickupSlots, usePostponePublic } from "../api/postpone.api";
import { StyledMainActionButton } from "../components/styled/StyledMainActionButton";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

export const PostponePage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(false);
  const [displayResult, setDisplayResult] = useState(false);
  const [newPickupDate, setNewPickupDate] = useState<Date | null>(null);

  const id = window.location.pathname.split("/")[2];

  const { mutateAsync: postpone } = usePostponePublic();
  const {
    data: availablePickupSlots,
    isLoading: isLoadingAvailablePickupSlots,
    isError: isErrorAvailablePickupSlots,
    isSuccess: isSuccessAvailablePickupSlots,
  } = useGetAvailablePickupSlots(id);

  const handlePostpone = async () => {
    if (!newPickupDate) return;
    setIsLoading(true);
    try {
      await postpone({
        id: id || "",
        payload: {
          token: searchParams.get("token") || "",
          pickupDate: new Date(new Date(newPickupDate).setHours(12, 0, 0, 0)).toISOString(),
        },
      });
    } catch (error) {
      setError(true);
    } finally {
      setIsLoading(false);
      setDisplayResult(true);
    }
  };

  const shouldDisableDate = (date: Date) => {
    const dateString = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
    return availablePickupSlots.some(
      (el: { date: string; slot: string }) => el.date.split("T")[0] === dateString,
    );
  };

  if (displayResult) {
    return (
      <div className="flex min-h-screen w-screen items-center justify-center bg-blue-300">
        <div className="mx-4 flex max-w-[600px] flex-col items-center gap-4 rounded-2xl bg-white px-6 py-6 md:px-16 md:py-12">
          <img src="/images/pelican-logo.png" className="h-24 w-24 sm:h-32 sm:w-32" />
          <div className="flex w-full flex-col items-center gap-6">
            {error ? (
              <>
                <h1 className="w-full text-center text-lg font-medium text-black sm:text-xl">
                  Nous n'avons pas pu reporter l'enlèvement
                </h1>
                <p className="text-start text-sm text-red-800">
                  Cela peut arriver si le lien a déjà été utilisé. Vérifiez vos mails ou contactez
                  le support.
                </p>
              </>
            ) : (
              <>
                <h1 className="w-full text-center text-lg font-medium text-black sm:text-xl">
                  L'enlèvement a bien été reporté
                </h1>
                <p className="text-start text-sm text-gray-600">
                  Vous allez recevoir un mail avec la nouvelle date d'enlèvement et un nouveau lien
                  pour reporter l'enlèvement si nécessaire.
                </p>
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen w-screen items-center justify-center bg-blue-300">
      <div className="mx-4 flex max-w-[600px] flex-col items-center gap-4 rounded-2xl bg-white px-6 py-6 md:px-16 md:py-12">
        <img src="/images/pelican-logo.png" className="h-24 w-24 sm:h-32 sm:w-32" />
        <div className="flex w-full flex-col items-center gap-6">
          <h1 className="w-full text-center text-lg font-medium text-black sm:text-xl">
            Souhaitez vous reporter l'enlèvement au prochain créneau d'enlèvement ?
          </h1>
          <p className="text-start text-sm text-gray-600">
            Si vous ne souhaitez pas reporter l'enlèvement, vous pouvez simplement fermer cette
            page.
          </p>
          <DatePicker
            disabled={isLoadingAvailablePickupSlots || isErrorAvailablePickupSlots}
            value={newPickupDate}
            onChange={(date) => setNewPickupDate(date)}
            disablePast
            shouldDisableDate={(date) => !shouldDisableDate(date)}
          />
          {isSuccessAvailablePickupSlots && availablePickupSlots.length === 0 && (
            <span className="text-center text-sm text-red-800">
              Il semblerait que nous n'ayont pas réussi à trouver de créneau disponible. Veuillez
              nous contacter pour reporter l'enlèvement.
            </span>
          )}
          <div className="flex w-fit flex-row gap-2 pt-6">
            <StyledMainActionButton
              disabled={!newPickupDate}
              isLoading={isLoading}
              type="button"
              text="Reporter l'enlèvement"
              onClick={handlePostpone}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
